//PaymentSuccess.jsx
import React, { useEffect, useState } from 'react';
import { jsPDF } from 'jspdf';
import { useNavigate } from 'react-router-dom';
import logo from '../../shreeohmlogo.png';
import styles from './PaymentSuccess.module.css';
import axios from 'axios';
import config from '../../config/config';

const PaymentSuccess = () => {
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
   const navigate = useNavigate();

 useEffect(() => {
  const query = new URLSearchParams(window.location.search);
  const encodedData = query.get('data');

  if (encodedData) {
    try {
      const decoded = atob(encodedData);
      const parsed = JSON.parse(decoded);
      setPaymentInfo(parsed);

      // Update payment status in the backend
      const updatePaymentStatus = async () => {
        try {
          const response = await axios.post(`${config.apiBaseUrl}/api/purchased-products/update-status`, {
            transaction_uuid: parsed.transaction_uuid,
            status: 'success', // Force it to 'success'
            transaction_code: parsed.transaction_code,
            total_amount: parsed.total_amount,
            product_code: parsed.product_code
          });

          if (response.data.success) {
            console.log("✅ Payment status updated to PAID");
          } else {
            console.log("⚠️ Backend failed to update status");
          }
        } catch (error) {
          console.error("❌ Error updating payment status:", error);
        }
      };

      updatePaymentStatus();
    } catch (err) {
      console.error("❌ Failed to decode payment data:", err);
    } finally {
      setIsLoading(false);
    }
  } else {
    setIsLoading(false);
  }
}, []);


  const formatCurrency = (amount) => {
    return `NPR ${parseFloat(amount).toLocaleString('en-NP', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const downloadPDFReceipt = async () => {
    if (!paymentInfo) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = pageWidth / 2;
    
    try {
      // Add company logo
      const img = new Image();
      img.src = logo;
      
      await new Promise((resolve) => {
        img.onload = () => {
          const logoWidth = 50;
          const logoHeight = (img.height * logoWidth) / img.width;
          
          doc.addImage(
            img, 
            'PNG', 
            centerX - (logoWidth/2), 
            20, 
            logoWidth, 
            logoHeight
          );
          resolve();
        };
      });
    } catch (error) {
      console.error('Error loading logo:', error);
      doc.setFontSize(24);
      doc.setTextColor(40, 167, 69);
      doc.text('Shree Ohm', centerX, 30, { align: 'center' });
    }
    
    // Add header
    doc.setFontSize(22);
    doc.setTextColor(40, 167, 69);
    doc.text('PAYMENT RECEIPT', centerX, 80, { align: 'center' });
    
    // Add company info
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text('123 Business Street, City, Nepal', centerX, 90, { align: 'center' });
    doc.text(`Receipt #: ${paymentInfo.transaction_code}`, centerX, 96, { align: 'center' });
    
    // Add divider line
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 105, pageWidth - 20, 105);
    
    // Add transaction details
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('Transaction Summary', 20, 115);
    
    doc.setFontSize(12);
    let yPosition = 125;
    
    const details = [
      { label: 'Date:', value: new Date().toLocaleString() },
      { label: 'Status:', value: paymentInfo.status },
      { label: 'Transaction ID:', value: paymentInfo.transaction_uuid },
      { label: 'Amount Paid:', value: formatCurrency(paymentInfo.total_amount) },
      { label: 'Product:', value: paymentInfo.product_code },
      { label: 'Payment Method:', value: 'Credit Card' }
    ];
    
    details.forEach(item => {
      doc.setTextColor(100, 100, 100);
      doc.text(item.label, 20, yPosition);
      doc.setTextColor(0, 0, 0);
      doc.text(item.value.toString(), 70, yPosition);
      yPosition += 8;
    });
    
    // Add thank you message
    doc.setFontSize(14);
    doc.setTextColor(40, 167, 69);
    doc.text('Thank you for your business!', centerX, yPosition + 15, { align: 'center' });
    
    // Add footer
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text('For any inquiries, please contact support@shreeohm.com', centerX, 280, { align: 'center' });
    doc.text('© 2023 Shree Ohm. All rights reserved.', centerX, 286, { align: 'center' });
    
    // Save the PDF
    doc.save(`shreeohm_receipt_${paymentInfo.transaction_code}.pdf`);
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Processing your payment...</p>
      </div>
    );
  }

  if (!paymentInfo) {
    return (
      <div className={styles.errorContainer}>
        <h2>⚠️ Payment Information Not Found</h2>
        <p>We couldn't retrieve your payment details. Please contact support.</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.checkmark}>
            <svg viewBox="0 0 24 24">
              <path fill="currentColor" d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.68L7.91,10.59L6.5,12L11,16.5Z" />
            </svg>
          </div>
          <h1>Payment Successful!</h1>
          <p className={styles.subheader}>Thank you for your purchase</p>
        </div>

        <div className={styles.details}>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Status:</span>
            <span className={styles.detailValue}>{paymentInfo.status}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Date:</span>
            <span className={styles.detailValue}>{new Date().toLocaleString()}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Transaction Code:</span>
            <span className={styles.detailValue}>{paymentInfo.transaction_code}</span>
          </div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Transaction ID:</span>
            <span className={styles.detailValue}>{paymentInfo.transaction_uuid}</span>
          </div>
          <div className={styles.detailRow}>
  <span className={styles.detailLabel}>Total Paid:</span>
  <span className={styles.detailValue}>
    NPR {paymentInfo?.total_amount?.toLocaleString()}
  </span>
</div>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Product Code:</span>
            <span className={styles.detailValue}>{paymentInfo.product_code}</span>
          </div>
        </div>

        <div className={styles.actions}>
          <button 
            className={styles.primaryButton} 
            onClick={downloadPDFReceipt}
          >
            Download Receipt (PDF)
          </button>
          <button className={styles.secondaryButton} onClick={() => navigate('/')} > Purchase</button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;